/**
 * <gsd-product> — variant selection, gallery switching, quantity stepper and copy-link for the product page.
 * The add to cart itself is handled by gsd-cart.js (the form carries data-gsd-add).
 * Translated strings come from data attributes on the element.
 */
class GsdProduct extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[data-variants]');
    this.variants = data ? JSON.parse(data.textContent) : [];
    this.form = this.querySelector('form.gsd-product__form');
    this.idInput = this.querySelector('[data-variant-id]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.addButton = this.querySelector('[data-add-button]');
    this.label = this.querySelector('[data-gsd-label]');
    this.stock = this.querySelector('[data-stock]');
    this.priceEl = this.querySelector('[data-price]');
    this.compareEl = this.querySelector('[data-compare-price]');
    this.skuWrap = this.querySelector('[data-sku-wrap]');
    this.skuEl = this.querySelector('[data-sku]');

    this.current = this.variants.find((v) => String(v.id) === this.idInput?.value) || this.variants[0];

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-value]')) this.onOptionChange();
    });

    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumb));
    });

    this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.step(1));
    this.qtyInput?.addEventListener('change', () => this.clampQuantity());

    this.querySelector('[data-share]')?.addEventListener('click', (event) => this.copyLink(event.currentTarget));

    this.markUnavailable();
    this.setupSticky();
    this.setupPayLater();
    this.watchGalleryHeight();
  }

  /** The PayPal "pay in 4" message is added to the page by the PayPal app; keep it right under the add to cart. */
  setupPayLater() {
    const slot = this.querySelector('[data-paylater]');
    if (!slot) return;
    const place = () => {
      const message = document.getElementById('paypal-upstream-messaging-product');
      if (message && message.parentElement !== slot) slot.appendChild(message);
    };
    place();
    this.payLaterObserver = new MutationObserver(place);
    this.payLaterObserver.observe(this, { childList: true, subtree: true });
  }

  /** A gallery taller than the screen cannot stay pinned (its bottom would be out of reach): let it scroll with the page. */
  watchGalleryHeight() {
    const gallery = this.querySelector('.gsd-product__gallery');
    if (!gallery || !window.ResizeObserver) return;
    const check = () => {
      // A fixed allowance for the header, so this does not flip back and forth when the position changes.
      gallery.classList.toggle('is-tall', gallery.offsetHeight > window.innerHeight - 110);
    };
    this.galleryObserver = new ResizeObserver(check);
    this.galleryObserver.observe(gallery);
    window.addEventListener('resize', check, { passive: true });
    check();
  }

  disconnectedCallback() {
    this.galleryObserver?.disconnect();
    this.payLaterObserver?.disconnect();
    this.stickyObserver?.disconnect();
    this.stickyMutations?.disconnect();
    this.stickySize?.disconnect();
  }

  /* Sticky add to cart -------------------------------------------------------------------------- */

  setupSticky() {
    this.sticky = this.querySelector('[data-sticky]');
    const buy = this.querySelector('.gsd-product__buy');
    if (!this.sticky || !buy || !this.addButton) return;

    this.stickyAdd = this.sticky.querySelector('[data-sticky-add]');
    this.stickyLabel = this.sticky.querySelector('[data-sticky-label]');
    this.stickySelect = this.sticky.querySelector('[data-sticky-select]');
    this.stickyQty = this.sticky.querySelector('[data-sticky-qty]');
    this.stickyThumb = this.sticky.querySelector('[data-sticky-thumb]');
    this.stickyPrice = this.sticky.querySelector('[data-sticky-price]');
    this.stickyCompare = this.sticky.querySelector('[data-sticky-compare]');

    // Show the bar only while the main buy buttons are off screen and the footer is not in view.
    this.buyVisible = true;
    this.footerVisible = false;
    const update = () => this.setStickyVisible(!this.buyVisible && !this.footerVisible);

    this.stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === buy) this.buyVisible = entry.isIntersecting;
        else this.footerVisible = entry.isIntersecting;
      });
      update();
    });
    this.stickyObserver.observe(buy);
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (footer) this.stickyObserver.observe(footer);

    // Add to cart: hand over to the real form, so the cart alert and cart count work exactly as before.
    this.stickyAdd.addEventListener('click', () => {
      this.clampQuantity();
      if (this.qtyInput && this.stickyQty) this.qtyInput.value = this.stickyQty.value;
      this.addButton.click();
    });

    this.stickySelect?.addEventListener('change', () => {
      const variant = this.variants.find((item) => String(item.id) === this.stickySelect.value);
      if (!variant) return;
      this.querySelectorAll('.gsd-product__option').forEach((group, index) => {
        const input = Array.from(group.querySelectorAll('[data-option-value]')).find((item) => item.value === variant.options[index]);
        if (input) input.checked = true;
      });
      this.onOptionChange();
    });

    this.sticky.querySelector('[data-sticky-minus]')?.addEventListener('click', () => this.stepSticky(-1));
    this.sticky.querySelector('[data-sticky-plus]')?.addEventListener('click', () => this.stepSticky(1));
    this.stickyQty?.addEventListener('change', () => this.stepSticky(0));
    this.qtyInput?.addEventListener('change', () => this.syncSticky());

    // Mirror the main button: its text ("Adding…", "Added"), its busy state and whether it is disabled.
    this.stickyMutations = new MutationObserver(() => this.syncStickyButton());
    this.stickyMutations.observe(this.addButton, { attributes: true, attributeFilter: ['class', 'disabled', 'aria-busy'] });
    if (this.label) this.stickyMutations.observe(this.label, { childList: true, characterData: true, subtree: true });

    // The bar's height, so the add to cart alert can sit above it.
    this.stickySize = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--gsd-sticky-h', `${this.sticky.firstElementChild.offsetHeight}px`);
    });
    this.stickySize.observe(this.sticky.firstElementChild);

    this.syncSticky();
  }

  setStickyVisible(visible) {
    if (!this.sticky) return;
    this.sticky.classList.toggle('is-visible', visible);
    this.sticky.toggleAttribute('inert', !visible);
  }

  stepSticky(direction) {
    if (!this.stickyQty) return;
    const current = parseInt(this.stickyQty.value, 10) || 1;
    const next = Math.min(Math.max(1, current + direction), this.maxQuantity());
    if (direction > 0 && next === current) this.limitReached();
    this.stickyQty.value = next;
    if (this.qtyInput) this.qtyInput.value = next;
  }

  syncStickyButton() {
    if (!this.stickyAdd || !this.addButton) return;
    this.stickyAdd.disabled = this.addButton.disabled;
    this.stickyAdd.classList.toggle('is-loading', this.addButton.classList.contains('is-loading'));
    this.stickyAdd.classList.toggle('is-added', this.addButton.classList.contains('is-added'));
    if (this.stickyLabel && this.label) this.stickyLabel.textContent = this.label.textContent.trim();
  }

  /** Keep the bar in step with the page: price, option, quantity, picture and button. */
  syncSticky() {
    if (!this.sticky) return;
    const variant = this.current;
    if (variant) {
      if (this.stickyPrice) this.stickyPrice.textContent = variant.price;
      if (this.stickyCompare) {
        this.stickyCompare.textContent = variant.compare;
        this.stickyCompare.hidden = !variant.compare;
      }
      if (this.stickySelect) this.stickySelect.value = String(variant.id);
    }
    if (this.stickyQty && this.qtyInput) this.stickyQty.value = this.qtyInput.value;
    this.syncStickyButton();
  }

  selectedOptions() {
    return Array.from(this.querySelectorAll('.gsd-product__option')).map((group) => {
      const checked = group.querySelector('[data-option-value]:checked');
      return checked ? checked.value : null;
    });
  }

  findVariant(options) {
    return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  onOptionChange() {
    const variant = this.findVariant(this.selectedOptions());
    this.markUnavailable();
    if (!variant) {
      this.setUnavailable();
      return;
    }
    this.current = variant;
    this.render(variant);
  }

  /**
   * Hide values that cannot be bought together with the other chosen options, so customers only see what is in stock.
   * If nothing at all is in stock, the values stay visible (struck through) so the page is not empty.
   */
  markUnavailable() {
    const groups = Array.from(this.querySelectorAll('.gsd-product__option'));
    const anyAvailable = this.variants.some((variant) => variant.available);
    const selected = this.selectedOptions();
    let changed = false;

    groups.forEach((group, index) => {
      const inputs = Array.from(group.querySelectorAll('[data-option-value]'));
      inputs.forEach((input) => {
        const candidate = selected.slice();
        candidate[index] = input.value;
        const variant = this.findVariant(candidate);
        const unavailable = !variant || !variant.available;
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('is-unavailable', unavailable);
        const hide = unavailable && anyAvailable;
        input.hidden = hide;
        if (label) label.hidden = hide;
      });

      // The chosen value just disappeared: move to the first one that is still on sale.
      const checked = inputs.find((input) => input.checked);
      if (checked && checked.hidden) {
        const fallback = inputs.find((input) => !input.hidden);
        if (fallback) {
          fallback.checked = true;
          selected[index] = fallback.value;
          changed = true;
        }
      }
    });

    if (changed) this.onOptionChange();
  }

  render(variant) {
    if (this.idInput) {
      this.idInput.value = variant.id;
      // The express checkout buttons (Shop Pay) watch this field to buy the chosen option.
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.priceEl) this.priceEl.textContent = variant.price;
    if (this.compareEl) {
      this.compareEl.textContent = variant.compare;
      this.compareEl.hidden = !variant.compare;
    }

    if (this.skuWrap) {
      this.skuWrap.hidden = !variant.sku;
      if (this.skuEl) this.skuEl.textContent = variant.sku;
    }

    this.setStock(variant.message, !variant.available, variant.low);
    this.setButton(variant.available);
    this.clampQuantity();
    this.syncSticky();

    if (variant.media) this.showMedia(variant.media);

    if (this.variants.length > 1 && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  setStock(message, out, low) {
    if (!this.stock) return;
    this.stock.textContent = message;
    this.stock.hidden = !message;
    this.stock.classList.toggle('is-out', Boolean(out));
    this.stock.classList.toggle('is-low', Boolean(low) && !out);
  }

  setUnavailable() {
    this.setStock(this.dataset.unavailableMessage, true);
    this.setButton(false, this.dataset.unavailable);
  }

  setButton(available, text) {
    if (!this.addButton) return;
    this.addButton.disabled = !available;
    if (this.label) {
      const next = text || (available ? this.dataset.addLabel : this.dataset.soldLabel);
      this.label.dataset.original = next;
      this.label.textContent = next;
    }
  }

  showMedia(id) {
    const key = String(id);
    const slide = this.querySelector(`.gsd-product__slide[data-media-id="${key}"]`);
    if (!slide) return;
    this.querySelectorAll('.gsd-product__slide[data-media-id]').forEach((item) => {
      item.hidden = item !== slide;
      // A video that is no longer on show stops playing.
      if (item !== slide) item.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      const active = thumb.dataset.thumb === key;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', String(active));
    });
    const frame = this.querySelector('.gsd-product__frame');
    if (frame && slide.dataset.ratio) frame.style.setProperty('--gsd-ratio', slide.dataset.ratio);
    const image = slide.querySelector('img');
    if (image && this.form) this.form.dataset.image = image.currentSrc || image.src;
    if (image && this.stickyThumb) {
      try {
        const url = new URL(image.currentSrc || image.src, window.location.href);
        url.searchParams.set('width', '160');
        this.stickyThumb.src = url.toString();
      } catch (error) {
        this.stickyThumb.src = image.currentSrc || image.src;
      }
    }
  }

  // Limited Edition: tell the shopper why the quantity stops at 1
  limitReached() {
    const limit = this.form ? parseInt(this.form.dataset.limit, 10) || 0 : 0;
    if (limit > 0 && this.maxQuantity() <= limit && window.gsdCart && window.gsdCart.notifyLimit) window.gsdCart.notifyLimit();
  }

  maxQuantity() {
    return this.current && this.current.max > 0 ? this.current.max : Infinity;
  }

  clampQuantity() {
    if (!this.qtyInput) return;
    let value = parseInt(this.qtyInput.value, 10);
    if (Number.isNaN(value) || value < 1) value = 1;
    this.qtyInput.value = Math.min(value, this.maxQuantity());
  }

  step(direction) {
    if (!this.qtyInput) return;
    const before = parseInt(this.qtyInput.value, 10) || 1;
    this.qtyInput.value = before + direction;
    this.clampQuantity();
    if (direction > 0 && parseInt(this.qtyInput.value, 10) === before) this.limitReached();
    this.syncSticky();
  }

  async copyLink(button) {
    const label = button.querySelector('[data-share-label]') || button;
    const original = label.textContent;
    try {
      await navigator.clipboard.writeText(window.location.href);
      label.textContent = button.dataset.copied || original;
      setTimeout(() => {
        label.textContent = original;
      }, 2000);
    } catch (error) {
      window.prompt('', window.location.href);
    }
  }
}

if (!customElements.get('gsd-product')) customElements.define('gsd-product', GsdProduct);
